/* eslint-disable no-useless-escape */

/**
 * This code is a mixin for the embedding video on external platforms.
 *
 * @requires `aspectRatio` from `@/utils/embedSelection.mixin.js` or locally configured.
 * @requires `fetching` from `props` or locally configured.
 * @requires `video` from `props` or locally configured.
 * @requires `livePlayer` from `props` or locally configured.
 * @requires `liveInfo` from `props` or locally configured.
 * @requires `isExternalSourceVideo` from `props` or locally configured.
 */

export default {
  data: () => ({
    radioActive: 0,
    embedVideo: {},
    embedCode: {
      useMenuItem: false,
    },
    version: new Date().getTime(),
    preloadEmbed: true,
    playOpensFullscreen: {
      native: false,
      script: false,
      iosFakeFullscreen: false,
    },
  }),
  computed: {
    embed: {
      get() {
        return this.getEmbedCode()
      },
      cache: false,
    },
  },

  methods: {
    paramsToObject(entries) {
      const result = {}
      for (const [key, value] of entries) {
        result[key] = value
      }
      return result
    },
    getEmbedCode(videoItem = null) {
      const video =
        videoItem ??
        (this.embedCode.useMenuItem ? this.menuItem : this.video) ??
        {}

      const isToReturnNull =
        this.fetching ||
        (!video && !this.livePlayer) ||
        (!video.video_player && !this.livePlayer)
      const isFullscreenScript =
        (this.playOpensFullscreen.script && this.embedSelected === 'general') ??
        false

      // params
      let paramSrc = video.video_player ?? this.livePlayer
      let paramIsLive = this.isPlayerLiveState ? '&isLive=true' : ''
      let paramStartTime =
        this.embedVideo.startTimeCheckbox && this.embedVideo.startTime
          ? `&startTime=${this.embedVideo.startTime}`
          : ''
      let paramPlayOpensFullscreen = this.playOpensFullscreen.native
        ? '&playOpensFullscreenNative=true'
        : isFullscreenScript
        ? '&playOpensFullscreen=true'
        : !this.playOpensFullscreen.iosFakeFullscreen
        ? '&iosFakeFullscreen=true'
        : ''
      let paramPreload = this.preloadEmbed ? '' : '&preload=false'

      // embed parts
      let embedStart = ''
      let embedStyles = 'style="border:none;"'
      if (this.radioActive === 1) {
        embedStart = `<div style="position:relative;padding-top:${this.aspectRatio};">`
        embedStyles = embedStyles.slice(0, -1)
        embedStyles += 'position:absolute;top:0;left:0;"'
      }
      let embedEnd = this.radioActive === 1 ? '</div>' : ''
      let embedFeatures =
        'allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture" allowfullscreen=true'

      // iframe parts
      const iframeID = `panda-${
        video.video_external_id || (this.liveInfo ?? {}).id
      }`
      let iframeWidth = this.radioActive === 0 ? this.embedVideo.width : '100%'
      let iframeHeight =
        this.radioActive === 0 ? this.embedVideo.height : '100%'

      // scripts
      const scriptPlayOpensFullscreen =
        isFullscreenScript || !this.playOpensFullscreen.iosFakeFullscreen
          ? `<script>if(!document.querySelector('script[src="https://player.pandavideo.com.br/api.v2.js"]')){let s=document.createElement('script');s.src='https://player.pandavideo.com.br/api.v2.js'; s.async=true; document.head.appendChild(s);} window.pandascripttag = window.pandascripttag || [];window.pandascripttag.push(function (){const panda_id_player = '${iframeID}';const p=new PandaPlayer(panda_id_player,{onReady(){p.loadWindowScreen({panda_id_player});}});})<\/script>`
          : ''

      // External Source Video
      if (this.isExternalSourceVideo) {
        const params = [paramStartTime, paramPlayOpensFullscreen, paramPreload]
        const stringParams = params
          .reduce((pValue, cValue) => {
            if (cValue) {
              return `${pValue}${cValue.replace('&', '')}&`
            }
            return pValue
          }, '')
          ?.slice(0, -1)
        const urlParams = new URLSearchParams(stringParams)
        const objParams = this.paramsToObject(urlParams.entries())

        const pushObject = {
          ...{
            l: video.pullzone_name,
            onError(e) {
              console.error(e)
            },
          },
          ...objParams,
        }

        let additionalElementChild = ''

        if (video.video_source === 'youtube') {
          pushObject['t'] = 'yt'
          additionalElementChild = `<iframe class"skip-lazy" width="560" height="315" src="${
            video.video_url
          }" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen style="display:none" ${
            this.showYoutubeControls
              ? 'data-style="height: 200%; top: -50%;max-height: inherit;pointer-events: none;"'
              : ''
          }></iframe>`
        } else if (video.video_source === 'vimeo') {
          pushObject['t'] = 'vimeo'
          additionalElementChild = `<iframe class"skip-lazy" width="560" height="315" src="${
            video.video_url
          }" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen style="display:none" ${
            this.showYoutubeControls
              ? 'data-style="height: 200%; top: -50%;max-height: inherit;pointer-events: none;"'
              : ''
          }></iframe>`
        } else {
          pushObject['v'] = video.video_external_id
        }
        const additionalExternalStyle =
          this.radioActive === 0
            ? `width: ${
                typeof iframeWidth === 'string' && iframeWidth.includes('%')
                  ? iframeWidth
                  : iframeWidth + 'px'
              }; height: ${
                typeof iframeHeight === 'string' && iframeHeight.includes('%')
                  ? iframeHeight
                  : iframeHeight + 'px'
              };"`
            : ''
        const externalId = `pandaexternal-${video.video_external_id}`
        const additionalStyleTag = !additionalExternalStyle
          ? `<style>#${externalId} { position: relative; padding-top: 56.25%; } #${externalId} > div, #${externalId} > iframe {position:absolute; top:0; left:0; width: 100%; height: 100%;}</style>`
          : `<style>#${externalId} > div, #${externalId} > iframe { width: 100%; height: 100%; position: relative; } #${externalId} { ${additionalExternalStyle} }</style>`
        return `${additionalStyleTag}<div id="${externalId}">${additionalElementChild}</div><script>if ( !document.querySelector( 'script[src="https://player${
          process.env.VUE_APP_ENV === 'staging' ? '-staging' : ''
        }.pandavideo.com.br/player.external.js"]' ) ){let s=document.createElement('script'); s.src='https://player${
          process.env.VUE_APP_ENV === 'staging' ? '-staging' : ''
        }.pandavideo.com.br/player.external.js'; s.async=true; document.head.appendChild(s);}window.pandaexternaltag=window.pandaexternaltag || []; window.pandaexternaltag.push(()=>{const player=new PandaExternalPlayer( 'pandaexternal-${
          video.video_external_id
        }', ${JSON.stringify(pushObject)});});</script>`
      }

      // embed return
      if (isToReturnNull) return ''

      if (this.embedSelected === 'elementor') {
        let video_external_id
        let pullzone_name
        let extraParam

        if (paramIsLive) {
          video_external_id = this.liveInfo.id
          pullzone_name = this.liveInfo.live_player
            .split('player-')[1]
            .split('.')[0]
          if (this.embedVideo.startTimeCheckbox) {
            extraParam = `, playerConfigs: { isLive: true, startTime: ${this.embedVideo.startTime} }`
          } else {
            extraParam = ', playerConfigs: { isLive: true }'
          }
        } else {
          video_external_id = video.video_external_id
          pullzone_name = video.pullzone_name
          extraParam = ''
          if (this.embedVideo.startTimeCheckbox) {
            extraParam = `, playerConfigs: { startTime: ${this.embedVideo.startTime} }`
          }
        }

        embedStart = `<script>if(!document.querySelector('script[src^="https://player.pandavideo.com.br/api.v2.js"]')){let s=document.createElement('script'); s.src='https://player.pandavideo.com.br/api.v2.js?nowprocket&version=${this.version}'; s.async=true; document.head.appendChild(s);} window.pandascripttag=window.pandascripttag || []; window.pandascripttag.push(function (){const p=new PandaPlayer('${video_external_id}',{onReady() {p.loadButtonInTime({fetchApi: true})}, library_id: '${pullzone_name}', video_id: '${video_external_id}', fetchPriority: "high", defaultStyle: true${extraParam}});});</script><div id="${video_external_id}"></div><style>.e-con.e-flex > .e-con-inner:has(iframe[id^="panda-${video_external_id}"]) {flex-wrap: nowrap;} .elementor-element:has(iframe[id^="panda-${video_external_id}"]) {width: 100%;}</style>`
        return `${embedStart}${scriptPlayOpensFullscreen}`
      }

      if (this.embedSelected === 'leadlovers') {
        embedStart = embedEnd = ''
        embedStyles = embedStyles.slice(0, -1)
        embedStyles += 'height:100%;max-height:100%;"'
      }

      if (this.embedSelected === 'eduzz') {
        iframeWidth = this.embedVideo.width
        iframeHeight = this.embedVideo.height
      }

      if (this.embedSelected === 'hotmart') {
        embedStyles = 'style="border:none;top:0;left:0"'
        iframeWidth = this.embedVideo.width
        iframeHeight = this.embedVideo.height
      }

      let embedToBeReturned = `${embedStart}<iframe id="${iframeID}" src="${paramSrc}${paramIsLive}${paramStartTime}${paramPlayOpensFullscreen}${paramPreload}" ${embedStyles} ${embedFeatures} width="${iframeWidth}" height="${iframeHeight}" fetchpriority="high"></iframe>${scriptPlayOpensFullscreen}${embedEnd}`
      if (this.isAbTest) {
        embedToBeReturned += `<script>if(!document.querySelector('script[src^="https://player.pandavideo.com.br/api.v2.js"]')){let s=document.createElement('script'); s.src='https://player.pandavideo.com.br/api.v2.js'; s.async=true; document.head.appendChild(s);} window.pandascripttag=window.pandascripttag || []; window.pandascripttag.push(function (){const p=new PandaPlayer('panda-${video.video_external_id}',{onReady() {p.loadButtonInTime({fetchApi: true})}, library_id: '${video.pullzone_name}', video_id: '${video.video_external_id}', defaultStyle: true});});</script>`
      }
      return embedToBeReturned
    },
  },
}
